import { Box, Avatar, Typography, useTheme } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { ReactNode } from "react";

interface StatItem {
  icon: ReactNode;
  value: number;
  label: string;
  color: "success" | "error" | "warning" | "info";
}

const ICON_SIZE = "1rem";
const AVATAR_SIZE = 24;

const StatBox = ({ stat }: { stat: StatItem }) => {
  const theme = useTheme();
  const mainColor = theme.palette[stat.color].main;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        bgcolor: `${mainColor}08`,
        px: { xs: 1.5, sm: 1.75 },
        py: { xs: 1.5, sm: 1.75 },
        borderBottom: `3px solid ${mainColor}`,
      }}
    >
      <Avatar
        sx={{
          bgcolor: mainColor,
          color: "white",
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
        }}
      >
        {stat.icon}
      </Avatar>
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: "1.375rem", sm: "1.5rem" },
            lineHeight: 1.1,
            fontWeight: 800,
            color: mainColor,
          }}
        >
          {stat.value}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          }}
        >
          {stat.label}
        </Typography>
      </Box>
    </Box>
  );
};

interface StatsGridProps {
  approved: number;
  rejected: number;
  pending: number;
  total: number;
}

export const StatsGrid = ({
  approved,
  rejected,
  pending,
  total,
}: StatsGridProps) => {
  const stats: StatItem[] = [
    {
      icon: <CheckCircleIcon sx={{ fontSize: ICON_SIZE }} />,
      value: approved,
      label: "Approved",
      color: "success",
    },
    {
      icon: <CancelIcon sx={{ fontSize: ICON_SIZE }} />,
      value: rejected,
      label: "Rejected",
      color: "error",
    },
    {
      icon: <PendingIcon sx={{ fontSize: ICON_SIZE }} />,
      value: pending,
      label: "Pending",
      color: "warning",
    },
    {
      icon: <AssignmentIcon sx={{ fontSize: ICON_SIZE }} />,
      value: total,
      label: "Total",
      color: "info",
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
        mb: "10px",
      }}
    >
      {stats.map((stat) => (
        <StatBox key={stat.label} stat={stat} />
      ))}
    </Box>
  );
};
